import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import PropTypes from 'prop-types';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Row, Col } from 'reactstrap';
import { pathComparator } from '@truqc/content-path';
import { contentSelector, labelSelector, newPathSelector, valueSelector } from '../../selectors/index';
import entityComposition from '../../../common/classes/entityComposition';
// eslint-disable-next-line import/no-named-as-default-member
import saveFormBehavior from '../../../../nativeBehaviors/saveFormBehavior';
import store from '../../../../reducers/storeRegistration';
import logger from '../../../../utils/logger';
import aiNativeBehaviorEvaluator from '../../../../nativeBehaviors/aiNativeBehaviorEvaluator';
import { evaluateSource } from '../../../../../behaviors/src/lib/evaluateSource';
import { toBehaviorError } from '../../../../utils/behaviorErrorHandler';
import { configBehaviorsFunc } from '../../../../nativeBehaviors/configBehaviors';
import { nativeBehaviorEvaluator } from '../../../../nativeBehaviors/nativeBehaviorEvaluator';
// Import helper functions
import { arrayToCollection, collectionToArray, formatTimestamp, ensureLimitedResultsPrefix } from './qualityAgentHelpers';
// Import components
import QualityAgentCompletionModal from './qualityAgentCompletionModal';
import QualityAgentErrorModal from './qualityAgentErrorModal';
import ChecksSettingsModal from './checksSettingsModal';
import RunQualityAgentButton from './runQualityAgentButton';
import HeaderSection from './headerSection';
import SummarySection from './summarySection';
import RunDetailsSection from './runDetailsSection';
import TabsSection from './tabsSection';
import usePriorRuns from './usePriorRuns';

// Main QualityAgentView component
const QualityAgentView = _ref => {
  let _ref$value = _ref.value,
    value = _ref$value === void 0 ? null : _ref$value,
    _ref$content = _ref.content,
    content = _ref$content === void 0 ? null : _ref$content,
    _ref$path = _ref.path,
    path = _ref$path === void 0 ? '/' : _ref$path,
    _ref$formActions = _ref.formActions,
    formActions = _ref$formActions === void 0 ? null : _ref$formActions,
    _ref$reduxEntity = _ref.reduxEntity,
    reduxEntity = _ref$reduxEntity === void 0 ? 'form' : _ref$reduxEntity,
    _ref$pendingOperation = _ref.pendingOperation,
    pendingOperation = _ref$pendingOperation === void 0 ? false : _ref$pendingOperation,
    _ref$notificationActi = _ref.notificationActions,
    notificationActions = _ref$notificationActi === void 0 ? null : _ref$notificationActi,
    _ref$defaultChecks = _ref.defaultChecks,
    defaultChecks = _ref$defaultChecks === void 0 ? null : _ref$defaultChecks;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isRunning = _useState2[0],
    setIsRunning = _useState2[1];
  const _useState3 = useState('checks'),
    _useState4 = _slicedToArray(_useState3, 2),
    activeTab = _useState4[0],
    setActiveTab = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isSettingsOpen = _useState6[0],
    setIsSettingsOpen = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isCompletionModalOpen = _useState8[0],
    setIsCompletionModalOpen = _useState8[1];
  const _useState9 = useState(null),
    _useState0 = _slicedToArray(_useState9, 2),
    completedSummary = _useState0[0],
    setCompletedSummary = _useState0[1];
  const _useState1 = useState(false),
    _useState10 = _slicedToArray(_useState1, 2),
    isErrorModalOpen = _useState10[0],
    setIsErrorModalOpen = _useState10[1];

  // Determine initial resolvedDefaultChecks - if already expanded, use directly
  // Otherwise initialize as empty and resolve in useEffect
  const initialResolvedDefaultChecks = useMemo(() => {
    if (defaultChecks && typeof defaultChecks === 'object') {
      // If it has includes, we need to resolve them async, so start with empty
      if (defaultChecks.includes && Array.isArray(defaultChecks.includes)) {
        return {};
      }
      // Already expanded - use directly
      return defaultChecks;
    }
    return {};
  }, [defaultChecks]);
  const _useState11 = useState(initialResolvedDefaultChecks),
    _useState12 = _slicedToArray(_useState11, 2),
    resolvedDefaultChecks = _useState12[0],
    setResolvedDefaultChecks = _useState12[1];

  // Use defaultChecks directly if provided (already expanded in template)
  useEffect(() => {
    let cancelled = false;
    if (defaultChecks && typeof defaultChecks === 'object') {
      // Check if it has includes that need to be resolved
      if (defaultChecks.includes && Array.isArray(defaultChecks.includes)) {
        // Has includes - need to resolve them
        const loadDefaultChecks = async () => {
          try {
            const state = store.getState();
            const active = state[reduxEntity].active;
            const nativeFunctions = {
              errorHandler: err => {
                logger('Error loading defaultChecks:', err);
                throw toBehaviorError(err, 'Error loading defaultChecks');
              },
              evaluator: nativeBehaviorEvaluator.bind(null, active, reduxEntity),
              configBehaviors: configBehaviorsFunc
            };

            // Create a constantBehavior source with includes to load the file
            const source = {
              type: 'constantBehavior',
              value: defaultChecks
            };

            // Evaluate the behavior source to resolve includes
            const resolved = await evaluateSource(source, content || {}, path, {}, nativeFunctions);

            // Resolved should be an object with check definitions
            if (!cancelled) {
              if (resolved && typeof resolved === 'object') {
                setResolvedDefaultChecks(resolved);
              } else {
                setResolvedDefaultChecks({});
              }
            }
          } catch (error) {
            if (!cancelled) {
              logger('Error resolving defaultChecks:', error);
              setResolvedDefaultChecks({});
            }
          }
        };
        loadDefaultChecks();
      }
      // If already expanded, initialResolvedDefaultChecks should have handled it
    } else {
      // No defaultChecks - ensure it's empty
      setResolvedDefaultChecks({});
    }
    return () => {
      cancelled = true;
    };
  }, [defaultChecks, content, path, reduxEntity]);

  // Parse data from value
  const data = value || {};
  const _data$checks = data.checks,
    checksData = _data$checks === void 0 ? {} : _data$checks,
    _data$summary = data.summary,
    summaryData = _data$summary === void 0 ? {} : _data$summary,
    _data$settings = data.settings,
    settingsData = _data$settings === void 0 ? {} : _data$settings,
    _data$suggestedFollow = data.suggestedFollowups,
    suggestedFollowupsData = _data$suggestedFollow === void 0 ? {} : _data$suggestedFollow,
    _data$priorRuns = data.priorRuns,
    priorRunsData = _data$priorRuns === void 0 ? {} : _data$priorRuns;

  // Convert checks object to sorted array
  const checksArray = useMemo(() => {
    const checks = checksData || {};
    const arr = Object.entries(checks).map(_ref2 => {
      let _ref3 = _slicedToArray(_ref2, 2),
        id = _ref3[0],
        check = _ref3[1];
      return _objectSpread({
        id
      }, check);
    });
    return arr.sort(pathComparator('_created'));
  }, [checksData]);

  // Convert followups to sorted array
  const followupsArray = useMemo(() => {
    const suggestedFollowups = suggestedFollowupsData || {};
    const arr = Object.entries(suggestedFollowups).map(_ref4 => {
      let _ref5 = _slicedToArray(_ref4, 2),
        id = _ref5[0],
        followup = _ref5[1];
      return _objectSpread({
        id
      }, followup);
    });
    return arr.sort((a, b) => (a.priority || 999) - (b.priority || 999));
  }, [suggestedFollowupsData]);

  // Count unanalyzed followups
  const unanalyzedFollowupsCount = useMemo(() => followupsArray.filter(f => !f.analysis).length, [followupsArray]);

  // Use destructured values
  const checks = checksData;
  const summary = summaryData;
  const settings = settingsData;
  const priorRuns = priorRunsData;

  // Use custom hook for prior runs
  const priorRunsArray = usePriorRuns(priorRunsData);

  // Handler for opening/closing settings modal
  const handleOpenSettings = useCallback(() => {
    setIsSettingsOpen(true);
  }, []);
  const handleCloseSettings = useCallback(() => {
    setIsSettingsOpen(false);
  }, []);

  // Handlers for modal close callbacks
  const handleCloseCompletionModal = useCallback(() => {
    setIsCompletionModalOpen(false);
  }, []);
  const handleCloseErrorModal = useCallback(() => {
    setIsErrorModalOpen(false);
  }, []);

  // Handler for re-running quality agent analysis
  const handleReRun = useCallback(async () => {
    if (pendingOperation) {
      notificationActions.info({
        message: 'Waiting for changes to save.'
      }, {
        autoClose: 3000
      });
      return;
    }
    setIsRunning(true);
    try {
      var _content$formType, _report$summary, _report$summary2, _report$summary3, _report$summary4;
      // Get settings checks from the current value
      const settingsChecks = settings.checks || {};
      const formTitle = (content === null || content === void 0 ? void 0 : content.title) || '';
      const formTypeName = (content === null || content === void 0 || (_content$formType = content.formType) === null || _content$formType === void 0 ? void 0 : _content$formType.name) || '';

      // Ensure all queries have the correct limited results prefix
      const inputChecks = collectionToArray(settingsChecks).filter(check => check.enabled !== false) // default to enabled if not set
      .map(check => _objectSpread(_objectSpread({}, check), {}, {
        query: ensureLimitedResultsPrefix(check.query || '', formTypeName, formTitle)
      }));
      if (inputChecks.length === 0) {
        notificationActions.info({
          message: 'No quality agent checks enabled. Please enable checks in settings.'
        }, {
          autoClose: 5000
        });
        setIsRunning(false);
        return;
      }

      // Save current run to priorRuns before running new analysis
      const updatedPriorRuns = _objectSpread({}, priorRuns);
      if (summary.timestamp && Object.keys(checks).length > 0) {
        // Add current run to priorRuns keyed by timestamp
        updatedPriorRuns[summary.timestamp] = {
          summary: _objectSpread({}, summary),
          checks: _objectSpread({}, checks)
        };

        // Prune to keep only the 3 most recent entries
        const sortedKeys = Object.keys(updatedPriorRuns).sort((a, b) => {
          const timeA = new Date(a).getTime();
          const timeB = new Date(b).getTime();
          return timeB - timeA; // Most recent first
        });
        if (sortedKeys.length > 3) {
          const keysToRemove = sortedKeys.slice(3);
          keysToRemove.forEach(key => {
            delete updatedPriorRuns[key];
          });
        }
      }

      // Call qualityAgent directly via AI service (no server pass-through needed)
      // Authentication is handled automatically via JWT token
      const response = await aiNativeBehaviorEvaluator.qualityAgent(inputChecks);

      // Transform arrays to collections
      const report = response.report;
      const reportChecks = report.checks || [];
      const reportFollowups = report.suggestedFollowups || [];
      const checksCollection = arrayToCollection(reportChecks);
      const suggestedFollowupsCollection = arrayToCollection(reportFollowups);

      // Build the content to save (preserving settings and priorRuns)
      const newSummary = {
        timestamp: report.timestamp,
        assessment: report.assessment,
        assessmentMessage: report.assessmentMessage,
        totalChecks: (_report$summary = report.summary) === null || _report$summary === void 0 ? void 0 : _report$summary.totalChecks,
        passed: (_report$summary2 = report.summary) === null || _report$summary2 === void 0 ? void 0 : _report$summary2.passed,
        failed: (_report$summary3 = report.summary) === null || _report$summary3 === void 0 ? void 0 : _report$summary3.failures,
        warnings: (_report$summary4 = report.summary) === null || _report$summary4 === void 0 ? void 0 : _report$summary4.warnings
      };
      const qualityAgentContent = {
        settings,
        priorRuns: updatedPriorRuns,
        summary: newSummary,
        checks: checksCollection,
        suggestedFollowups: suggestedFollowupsCollection
      };

      // Save using formActions.change
      const state = store.getState();
      const active = state[reduxEntity].active;
      await formActions.change(active.id, "content".concat(path), qualityAgentContent);
      // eslint-disable-next-line import/no-named-as-default-member
      await saveFormBehavior.evaluate({}, reduxEntity);

      // Show completion modal
      setCompletedSummary(newSummary);
      setIsCompletionModalOpen(true);
    } catch (error) {
      logger('Error running quality agent:', error);
      setIsErrorModalOpen(true);
    } finally {
      setIsRunning(false);
    }
  }, [pendingOperation, notificationActions, settings, content, path, formActions, reduxEntity, priorRuns, summary, checks]);

  // If no checks data, show RunQualityAgentButton to allow running quality agent analysis
  if (Object.keys(checks).length === 0) {
    return /*#__PURE__*/React.createElement("div", {
      className: "quality-agent"
    }, /*#__PURE__*/React.createElement(RunQualityAgentButton, {
      content: content,
      path: path,
      formActions: formActions,
      reduxEntity: reduxEntity,
      pendingOperation: pendingOperation,
      notificationActions: notificationActions,
      onOpenSettings: handleOpenSettings
    }), /*#__PURE__*/React.createElement(ChecksSettingsModal, {
      isOpen: isSettingsOpen,
      onClose: handleCloseSettings,
      checksCollection: settings.checks,
      defaultChecks: resolvedDefaultChecks,
      path: path,
      formActions: formActions,
      reduxEntity: reduxEntity,
      content: content,
      notificationActions: notificationActions,
      pendingOperation: pendingOperation
    }));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "quality-agent"
  }, /*#__PURE__*/React.createElement(HeaderSection, {
    summary: summary,
    isRunning: isRunning,
    pendingOperation: pendingOperation,
    onReRun: handleReRun,
    onOpenSettings: handleOpenSettings,
    formatTimestamp: formatTimestamp
  }), /*#__PURE__*/React.createElement(Row, {
    className: "mb-4"
  }, /*#__PURE__*/React.createElement(Col, {
    lg: 8
  }, /*#__PURE__*/React.createElement(SummarySection, {
    summary: summary
  })), /*#__PURE__*/React.createElement(Col, {
    lg: 4
  }, /*#__PURE__*/React.createElement(RunDetailsSection, {
    summary: summary,
    priorRunsArray: priorRunsArray
  }))), /*#__PURE__*/React.createElement(TabsSection, {
    activeTab: activeTab,
    onTabChange: setActiveTab,
    checksArray: checksArray,
    followupsArray: followupsArray,
    unanalyzedFollowupsCount: unanalyzedFollowupsCount,
    content: content,
    path: path,
    formActions: formActions,
    reduxEntity: reduxEntity,
    pendingOperation: pendingOperation,
    notificationActions: notificationActions
  }), /*#__PURE__*/React.createElement(ChecksSettingsModal, {
    isOpen: isSettingsOpen,
    onClose: handleCloseSettings,
    checksCollection: settings.checks,
    defaultChecks: resolvedDefaultChecks,
    path: path,
    formActions: formActions,
    reduxEntity: reduxEntity,
    content: content,
    notificationActions: notificationActions,
    pendingOperation: pendingOperation
  }), completedSummary && /*#__PURE__*/React.createElement(QualityAgentCompletionModal, {
    isOpen: isCompletionModalOpen,
    onClose: handleCloseCompletionModal,
    summary: completedSummary
  }), /*#__PURE__*/React.createElement(QualityAgentErrorModal, {
    isOpen: isErrorModalOpen,
    onClose: handleCloseErrorModal
  }));
};
QualityAgentView.propTypes = {
  value: PropTypes.shape({
    checks: PropTypes.shape({}),
    summary: PropTypes.shape({
      passed: PropTypes.number,
      failed: PropTypes.number,
      warnings: PropTypes.number,
      totalChecks: PropTypes.number,
      timestamp: PropTypes.string,
      assessment: PropTypes.string,
      assessmentMessage: PropTypes.string
    }),
    settings: PropTypes.shape({
      checks: PropTypes.shape({})
    }),
    suggestedFollowups: PropTypes.shape({}),
    priorRuns: PropTypes.shape({})
  }),
  content: PropTypes.shape({
    title: PropTypes.string,
    formType: PropTypes.shape({
      name: PropTypes.string
    })
  }),
  path: PropTypes.string,
  formActions: PropTypes.shape({
    change: PropTypes.func.isRequired
  }),
  reduxEntity: PropTypes.string,
  pendingOperation: PropTypes.bool,
  notificationActions: PropTypes.shape({
    info: PropTypes.func
  }),
  defaultChecks: PropTypes.oneOfType([PropTypes.shape({
    includes: PropTypes.arrayOf(PropTypes.string)
  }), PropTypes.shape({}) // Already resolved checks object
  ])
};

// Removed defaultProps - using default parameters in function signature instead

export { QualityAgentView as UncomposedQualityAgentView };
export default entityComposition(QualityAgentView, [], {
  value: valueSelector,
  path: newPathSelector,
  label: labelSelector,
  content: contentSelector,
  defaultChecks: (state, props) => props.format && props.format.defaultChecks !== undefined ? props.format.defaultChecks : null,
  pendingOperation: state => state.form.pendingOperation
}, ['form', 'notification']);